(() => {
  'use strict';

  const GLOBAL_KEY = 'ZinVoltShopifyForms';
  const META_LEAD_POLICY = window.ZinVoltMetaLeadPolicy || null;
  const ENGINE_SELECTOR = '[data-zv-shopify-engine][data-zv-form-id]';
  const SOURCE_SELECTOR = 'form[data-zv-shopify-form][data-zv-form-id]';
  const WAIT_FOR_FORM_MS = 30000;
  const WAIT_FOR_MAPPING_MS = 5000;
  const WAIT_FOR_SUCCESS_MS = 20000;
  const DUPLICATE_SUBMIT_COOLDOWN_MS = 90 * 1000;
  const DUPLICATE_STORAGE_PREFIX = 'zv_recent_form_success_';
  const META_PIXEL_ATTEMPT_OFFSETS_MS = Object.freeze([0, 250, 1000, 3000]);

  if (window[GLOBAL_KEY]?.initialise) {
    window[GLOBAL_KEY].initialise(document);
    return;
  }

  const FORM_ORDERS = {
    '1101485': ['First name', 'Email', 'Phone', 'Onderwerp', 'Ordernummer', 'Bericht', 'Bronpagina', 'Pagina URL'],
    '1101817': ['First name', 'Phone', 'Email', 'Adres', 'Situatie', 'Bron', 'Pagina URL', 'Event ID', 'FBP', 'FBC', 'Client User Agent'],
    '1101824': ['First name', 'Phone', 'Email', 'Postcode', 'Huisnummer', 'Jaarverbruik', 'Aantal zonnepanelen', 'Product', 'Bronpagina', 'Pagina URL', 'Event ID', 'FBP', 'FBC', 'Client User Agent'],
    '1101834': ['First name', 'Phone', 'Email', 'Contactvoorkeur', 'Energiecontract', 'Netafname per jaar', 'Teruglevering per jaar', 'Zonneopwek per jaar', 'Direct eigen verbruik', 'Verbruiksprofiel', 'Toekomstplannen', 'Rekenmethode', 'Rekenwaarden 2026', 'Rekenwaarden 2027', 'Verandering vanaf 2027', 'Besparing over 10 jaar', 'Indicatieve batterijcapaciteit', 'Stroom uit batterij per jaar', 'Huidige bespaarindicatie met batterij', 'Besparingsruimte met persoonlijk advies', 'Bron', 'Pagina URL', 'Event ID', 'FBP', 'FBC', 'Client User Agent'],
    '1101850': ['First name', 'Email', 'Bronpagina', 'Pagina URL']
  };

  // Preserve the existing first-party attribution/Google/CRM route even if the
  // separate Meta policy asset fails. Meta identity remains independently
  // fail-closed through EVENT_ID_FORMS and the browser dispatch gate below.
  const ATTRIBUTION_FORM_IDS = new Set(['1101817', '1101824', '1101834']);
  const ATTRIBUTION_FIELD_ORDER = [
    'Lead UUID',
    'Attribution Schema Version',
    'First Touch JSON',
    'Last Touch JSON',
    'Google Click ID Type',
    'Google Click ID',
    'Consent Snapshot ID',
    'Consent Snapshot JSON'
  ];
  const LEAD_EVENT_CONFIG = Object.freeze({
    '1101817': Object.freeze({
      leadType: 'quick_form',
      conversionRoute: 'Lead | Quick Form | Website',
      adsOptimizationEligible: true
    }),
    '1101824': Object.freeze({
      leadType: 'personal_advice',
      conversionRoute: 'Lead | Persoonlijk Advies | Website',
      adsOptimizationEligible: true
    }),
    '1101834': Object.freeze({
      leadType: 'salderingscheck_callback',
      conversionRoute: 'Lead | Salderingscheck | Website',
      adsOptimizationEligible: true
    })
  });

  ATTRIBUTION_FORM_IDS.forEach((formId) => {
    FORM_ORDERS[formId] = [...(FORM_ORDERS[formId] || []), ...ATTRIBUTION_FIELD_ORDER];
  });

  // One source of truth for non-personal form metadata. Only the forms that
  // are intentionally treated as Meta leads receive eventName "Lead".
  const FORM_TRACKING = Object.freeze({
    '1101485': Object.freeze({ formName: 'Contact', eventName: '' }),
    '1101817': Object.freeze({ formName: 'Snel energieadvies', eventName: 'Lead' }),
    '1101824': Object.freeze({ formName: 'Persoonlijk energieadvies', eventName: 'Lead' }),
    '1101834': Object.freeze({ formName: 'Salderingscheck telefonisch advies', eventName: 'Lead' }),
    '1101843': Object.freeze({ formName: 'Salderingscheck offerte', eventName: '' }),
    '1101850': Object.freeze({ formName: 'Nieuwsbrief', eventName: '' })
  });

  const EVENT_ID_FORMS = new Set(META_LEAD_POLICY?.eligibleFormIds || []);
  const EXPLICIT_PREVIEW_RUNTIME_REASONS = new Set([
    'non_production_host',
    'shopify_design_mode',
    'shopify_preview_parameter',
    'shopify_unpublished_theme'
  ]);
  const isExplicitPreviewRuntime = (reason) => EXPLICIT_PREVIEW_RUNTIME_REASONS.has(String(reason || ''));
  const PREVIEW_CHECK_COPY = Object.freeze({
    nl: 'Formulier gecontroleerd. In deze preview is niets verstuurd. Shopify, Salesdock en advertentiemeting zijn nog niet getest.',
    en: 'Form checked. Nothing was sent in this preview. Shopify, Salesdock and advertising measurement have not been tested.'
  });
  const previewCheckMessage = () => PREVIEW_CHECK_COPY[
    String(document.documentElement?.lang || 'nl').toLowerCase().startsWith('en') ? 'en' : 'nl'
  ];
  const browserLeadEventIds = new Set();
  const pendingBrowserLeadDispatches = new Map();
  const terminalBrowserLeadEventIds = new Set();

  const attemptMetaBrowserLeadDispatch = (tracking) => {
    if (tracking?.eventName !== META_LEAD_POLICY?.eventName || !EVENT_ID_FORMS.has(String(tracking.formId))) {
      return { status: 'not_applicable', warningCode: '' };
    }
    let currentConsentSnapshot = null;
    try {
      currentConsentSnapshot = window.ZinVoltLeadAttribution?.consentSnapshot?.() || null;
    } catch (error) {
      return { status: 'terminal', warningCode: 'meta_current_consent_read_failed' };
    }
    if (!isValidEventId(tracking.eventId, tracking.formId)) {
      return { status: 'terminal', warningCode: 'meta_browser_lead_invalid_event_id' };
    }
    if (!META_LEAD_POLICY?.canDispatchBrowserLead?.(tracking, currentConsentSnapshot)) {
      return { status: 'terminal', warningCode: 'meta_browser_lead_contract_gate_failed' };
    }
    if (browserLeadEventIds.has(tracking.eventId)) {
      return { status: 'already_sent', warningCode: '' };
    }
    let fbq = null;
    try {
      fbq = window.fbq;
    } catch (error) {
      return { status: 'terminal', warningCode: 'meta_browser_lead_dispatch_failed' };
    }
    if (typeof fbq !== 'function' || fbq.loaded !== true) {
      return { status: 'retryable_pixel_unavailable', warningCode: '' };
    }

    try {
      fbq(
        'trackSingle',
        META_LEAD_POLICY.datasetId,
        META_LEAD_POLICY.eventName,
        {},
        { eventID: tracking.eventId }
      );
      browserLeadEventIds.add(tracking.eventId);
      return { status: 'sent', warningCode: '' };
    } catch (error) {
      // A synchronous Pixel exception is terminal. Retrying an unknown partial
      // write could emit the same browser event twice.
      return { status: 'terminal', warningCode: 'meta_browser_lead_dispatch_failed' };
    }
  };

  const reportMetaBrowserLeadTerminalWarning = (tracking, warningCode) => {
    if (!warningCode) return;
    reportTrackingWarning(tracking?.formId, warningCode);
  };

  const runMetaBrowserLeadAttempt = (eventId, attemptIndex) => {
    const pending = pendingBrowserLeadDispatches.get(eventId);
    if (!pending) return false;

    const outcome = attemptMetaBrowserLeadDispatch(pending.tracking);
    if (outcome.status === 'sent' || outcome.status === 'already_sent') {
      pendingBrowserLeadDispatches.delete(eventId);
      return true;
    }

    if (outcome.status !== 'retryable_pixel_unavailable') {
      pendingBrowserLeadDispatches.delete(eventId);
      terminalBrowserLeadEventIds.add(eventId);
      reportMetaBrowserLeadTerminalWarning(pending.tracking, outcome.warningCode);
      return false;
    }

    const nextAttemptIndex = attemptIndex + 1;
    if (nextAttemptIndex >= META_PIXEL_ATTEMPT_OFFSETS_MS.length) {
      pendingBrowserLeadDispatches.delete(eventId);
      terminalBrowserLeadEventIds.add(eventId);
      reportMetaBrowserLeadTerminalWarning(pending.tracking, 'meta_pixel_unavailable');
      return false;
    }

    const retryDelay = META_PIXEL_ATTEMPT_OFFSETS_MS[nextAttemptIndex]
      - META_PIXEL_ATTEMPT_OFFSETS_MS[attemptIndex];
    try {
      pending.timeoutId = window.setTimeout(
        () => runMetaBrowserLeadAttempt(eventId, nextAttemptIndex),
        retryDelay
      );
      return true;
    } catch (error) {
      pendingBrowserLeadDispatches.delete(eventId);
      terminalBrowserLeadEventIds.add(eventId);
      reportMetaBrowserLeadTerminalWarning(pending.tracking, 'meta_pixel_retry_schedule_failed');
      return false;
    }
  };

  const dispatchMetaBrowserLead = (tracking) => {
    if (tracking?.eventName !== META_LEAD_POLICY?.eventName || !EVENT_ID_FORMS.has(String(tracking.formId))) return false;

    const eventId = String(tracking.eventId || '');
    if (!isValidEventId(eventId, tracking.formId)) {
      const outcome = attemptMetaBrowserLeadDispatch(tracking);
      reportMetaBrowserLeadTerminalWarning(tracking, outcome.warningCode);
      return false;
    }
    if (browserLeadEventIds.has(eventId) || pendingBrowserLeadDispatches.has(eventId)) return true;
    if (terminalBrowserLeadEventIds.has(eventId)) return false;

    pendingBrowserLeadDispatches.set(eventId, { tracking, timeoutId: null });
    return runMetaBrowserLeadAttempt(eventId, 0);
  };
  const isValidEventId = (value, formId = '') => (
    META_LEAD_POLICY?.isValidEventId?.(value, formId) === true
  );

  const isTechnicalEventIdField = (field) => field?.technical === 'event_id';
  const isTechnicalConsentSnapshotField = (field) => field?.technical === 'consent_snapshot';
  const isTechnicalTrackingField = (field) => Boolean(field?.technical);
  const TECHNICAL_TRACKING_FIELD_NAMES = new Set([
    'event id',
    'fbp',
    'fbc',
    'client user agent',
    'lead uuid',
    'attribution schema version',
    'first touch json',
    'last touch json',
    'google click id type',
    'google click id',
    'consent snapshot id',
    'consent snapshot json'
  ]);

  const createTechnicalEventIdField = (formId, eventId) => {
    if (!EVENT_ID_FORMS.has(String(formId))) return null;
    // Keep mapping the technical field with an explicit empty value whenever
    // identity creation is blocked (preview, design mode or no Web Crypto).
    // This clears any hydrated/stale control value while remaining invalid for
    // both the browser gate and Worker contract.
    const safeEventId = isValidEventId(eventId, formId) ? eventId : '';
    return {
      name: 'Event ID',
      value: safeEventId,
      aliases: 'Event ID|EventID',
      // Shopify Forms normally exposes the generated handle as eventId. Prefer
      // that stable identity when present, while keeping the exact field label
      // as a safe fallback for Shopify renderer variants.
      targetKey: 'eventId',
      targetLabels: 'Event ID',
      targetOptional: true,
      preferTargetKey: true,
      technical: 'event_id',
      sourceTag: 'INPUT',
      sourceType: 'text'
    };
  };


  const readCookie = (name) => {
    const prefix = `${name}=`;
    const parts = String(document.cookie || '').split(';');
    for (const part of parts) {
      const candidate = part.trim();
      if (!candidate.startsWith(prefix)) continue;
      const raw = candidate.slice(prefix.length);
      try {
        return decodeURIComponent(raw);
      } catch (error) {
        return raw;
      }
    }
    return '';
  };

  const cleanMetaCookieValue = (value) => {
    const textValue = String(value || '').trim();
    if (!textValue || textValue.length > 512) return '';
    return /^[A-Za-z0-9._:-]+$/.test(textValue) ? textValue : '';
  };

  const cleanUserAgent = (value) => {
    const textValue = String(value || '').trim();
    if (!textValue) return '';
    return textValue.slice(0, 1024);
  };

  const createTechnicalMetaSignalFields = (formId, tracking) => {
    if (!EVENT_ID_FORMS.has(String(formId))) return [];
    const consent = tracking.leadContext?.consent_snapshot || {};
    const allowsMarketingSignals = isValidEventId(tracking?.eventId, formId)
      && META_LEAD_POLICY?.isAllowedConsentState?.(consent) === true;
    const definitions = [
      {
        name: 'FBP',
        value: allowsMarketingSignals ? cleanMetaCookieValue(tracking.fbp) : '',
        targetKey: 'fbp',
        targetLabels: 'FBP',
        technical: 'fbp'
      },
      {
        name: 'FBC',
        value: allowsMarketingSignals ? cleanMetaCookieValue(tracking.fbc) : '',
        targetKey: 'fbc',
        targetLabels: 'FBC',
        technical: 'fbc'
      },
      {
        name: 'Client User Agent',
        value: allowsMarketingSignals ? cleanUserAgent(tracking.clientUserAgent) : '',
        targetKey: 'clientUserAgent',
        targetLabels: 'Client User Agent',
        technical: 'client_user_agent'
      }
    ];

    return definitions.map((definition) => ({
      ...definition,
      aliases: definition.name,
      targetOptional: true,
      preferTargetKey: true,
      sourceTag: 'INPUT',
      sourceType: 'text'
    }));
  };

  const createTechnicalAttributionFields = (formId, leadContext) => {
    const safeLeadContext = leadContext && typeof leadContext === 'object' ? leadContext : {};
    const canonicalLeadUuid = safeLeadContext.lead_uuid
      || safeLeadContext.submission_uuid
      || safeLeadContext.provisional_lead_uuid
      || '';
    if (!ATTRIBUTION_FORM_IDS.has(String(formId))) return [];

    const definitions = [
      ['Lead UUID', 'leadUuid', canonicalLeadUuid, 'lead_uuid'],
      ['Attribution Schema Version', 'attributionSchemaVersion', safeLeadContext.schema_version, 'attribution_schema'],
      ['First Touch JSON', 'firstTouchJson', safeLeadContext.first_touch_json, 'first_touch'],
      ['Last Touch JSON', 'lastTouchJson', safeLeadContext.last_touch_json, 'last_touch'],
      ['Google Click ID Type', 'googleClickIdType', safeLeadContext.google_click_id_type, 'click_id_type'],
      ['Google Click ID', 'googleClickId', safeLeadContext.google_click_id, 'click_id'],
      ['Consent Snapshot ID', 'consentSnapshotId', safeLeadContext.consent_snapshot_id, 'consent_snapshot_id'],
      ['Consent Snapshot JSON', 'consentSnapshotJson', safeLeadContext.consent_snapshot_json, 'consent_snapshot']
    ];

    return definitions.map(([name, targetKey, value, technical]) => ({
      name,
      value: String(value || ''),
      aliases: name,
      targetKey,
      targetLabels: name,
      // Optional during canary: tracking metadata may never block a lead.
      targetOptional: true,
      preferTargetKey: true,
      technical,
      sourceTag: 'INPUT',
      sourceType: 'hidden'
    }));
  };

  const reportTrackingWarning = (formId, code) => {
    try {
      console.warn('[ZinVolt Shopify Forms]', code, { form_id: String(formId || '') });
    } catch (error) {
      // Tracking diagnostics may never interfere with a customer submission.
    }
    try {
      document.dispatchEvent(new CustomEvent('zv:shopify-form-tracking-warning', {
        detail: { formId: String(formId || ''), code: String(code || 'tracking_warning') }
      }));
    } catch (error) {
      // CustomEvent support is diagnostic only.
    }
  };

  const currentEventSourceUrl = (formId) => {
    const canonicalUrl = document.querySelector?.('link[rel="canonical"]')?.href;
    const sourceUrl = canonicalUrl || window.location.href;
    if (META_LEAD_POLICY?.isEligibleFormId?.(formId)) {
      return META_LEAD_POLICY.canonicalizeEventSourceUrl?.(formId, sourceUrl) || '';
    }
    return META_LEAD_POLICY?.canonicalizeFirstPartyUrl?.(sourceUrl) || '';
  };

  const createTrackingContext = (rawFormId, rawFormName = '') => {
    const formId = String(rawFormId || '').trim();
    const configured = FORM_TRACKING[formId];
    const suppliedName = String(rawFormName || '').trim();
    // A preview check validates the native form contract only. Do not create
    // persistent attribution/lead identities or read advertising cookies for
    // a check that intentionally cannot create a customer submission.
    if (isExplicitPreviewRuntime(META_LEAD_POLICY?.runtimeBlockReason?.())) {
      return {
        formId,
        formName: configured?.formName || suppliedName || `Shopify Forms ${formId || 'onbekend'}`,
        eventName: configured?.eventName || '',
        eventId: '',
        eventSourceUrl: '',
        fbp: '',
        fbc: '',
        clientUserAgent: '',
        leadContext: null,
        eventIdReadbackConfirmed: false,
        consentSnapshotReadbackConfirmed: false,
        submittedAt: ''
      };
    }
    let leadContext = null;
    if (ATTRIBUTION_FORM_IDS.has(formId)) {
      try {
        leadContext = window.ZinVoltLeadAttribution?.createLeadContext?.(formId) || null;
      } catch (error) {
        reportTrackingWarning(formId, 'attribution_context_failed');
      }
      if (!leadContext) reportTrackingWarning(formId, 'attribution_context_unavailable');
    }
    const canonicalLeadUuid = leadContext?.lead_uuid
      || leadContext?.submission_uuid
      || leadContext?.provisional_lead_uuid
      || '';
    const consent = leadContext?.consent_snapshot || {};
    const eventSourceUrl = currentEventSourceUrl(formId);
    const eventId = META_LEAD_POLICY?.isProductionRuntime?.() && eventSourceUrl
      ? META_LEAD_POLICY.createEventId?.(formId, canonicalLeadUuid) || ''
      : '';
    const allowsMarketingSignals = isValidEventId(eventId, formId)
      && META_LEAD_POLICY?.isAllowedConsentState?.(consent) === true;
    return {
      formId,
      formName: configured?.formName || suppliedName || `Shopify Forms ${formId || 'onbekend'}`,
      eventName: configured?.eventName || '',
      // Stable across an ambiguous timeout/retry and shared by Meta browser/CAPI.
      eventId,
      eventSourceUrl,
      fbp: allowsMarketingSignals ? readCookie('_fbp') : '',
      fbc: allowsMarketingSignals ? readCookie('_fbc') : '',
      clientUserAgent: allowsMarketingSignals ? window.navigator?.userAgent || '' : '',
      leadContext,
      eventIdReadbackConfirmed: false,
      consentSnapshotReadbackConfirmed: false,
      submittedAt: ''
    };
  };

  const FIELD_ALIASES = {
    "indicatieve batterijcapaciteit": ["Indicative battery capacity"],
    "besparing over 10 jaar": ["Savings over 10 years"],
    "verandering vanaf 2027": ["Change from 2027"],
    "rekenwaarden 2027": ["Calculation assumptions 2027"],
    "rekenwaarden 2026": ["Calculation assumptions 2026"],
    "rekenmethode": ["Calculation method"],
    "toekomstplannen": ["Future plans"],
    "verbruiksprofiel": ["Consumption profile"],
    "direct eigen verbruik": ["Direct self-consumption"],
    "zonneopwek per jaar": ["Annual solar generation"],
    "teruglevering per jaar": ["Annual grid export"],
    "netafname per jaar": ["Annual grid import"],
    "energiecontract": ["Energy contract"],
    "contactvoorkeur": ["Contact preference"],
    "situatie": ["Situation"],
    "adres": ["Address"],
    "bericht": ["Message"],
    "ordernummer": ["Order number", "Order/serial number"],
    "onderwerp": ["Subject"],
    'first name': ['first name', 'voornaam', 'naam', 'name', 'full name', "First and last name"],
    'email': ['email', 'e mail', 'e-mailadres', 'e mailadres', "Email address"],
    'phone': ['phone', 'telefoon', 'telefoonnummer', 'mobile', 'mobiel', "Phone number"],
    'pagina url': ['pagina url', 'page url', 'url'],
    'bronpagina': ['bronpagina', 'source page', 'pagina bron'],
    'bron': ['bron', 'source'],
    'postcode': ['postcode', 'postal code', 'zip code'],
    'huisnummer': ['huisnummer', 'house number', 'housenumber'],
    'jaarverbruik': ['jaarverbruik', 'energieverbruik', 'annual electricity use', 'annual consumption', "Annual electricity consumption"],
    'aantal zonnepanelen': ['aantal zonnepanelen', 'zonnepanelen', 'solar panels', "Number of solar panels"],
    'totaal inclusief btw': ['totaal inclusief btw', 'totaalbedrag', 'total including vat'],
    'stroom uit batterij per jaar': ['stroom uit batterij per jaar', 'geschatte stroom uit batterij', "Annual energy from battery"],
    'huidige bespaarindicatie met batterij': ['huidige bespaarindicatie met batterij', 'huidige bespaarindicatie', "Current estimated battery savings"],
    'besparingsruimte met persoonlijk advies': ['besparingsruimte met persoonlijk advies', 'besparingsruimte met advies', "Potential savings with personal advice"]
  };

  const PHONE_COMPONENT_SELECTOR = [
    's-phone-field',
    'shopify-phone-field',
    '[data-phone-field]',
    '[data-field-type="phone"]',
    '[data-testid*="phone-field"]'
  ].join(',');

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const normalise = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const compactNormalise = (value = '') => normalise(value).replace(/\s+/g, '');

  const isAnnualUsageField = (fieldName = '') => normalise(fieldName) === 'jaarverbruik';

  const annualUsageBucket = (value = '') => {
    const raw = String(value || '').toLowerCase();
    const words = normalise(raw);

    if (
      /\b(weet ik (nog )?niet|ik weet (het )?(nog )?niet|weet (het )?niet|niet bekend|geen idee|onbekend|unknown|unsure)\b/.test(words)
      || /\b(do not know|don t know|dont know|not sure)\b/.test(words)
    ) {
      return 'unknown';
    }

    // Remove Dutch/English thousands separators before reading the limits.
    const numberText = raw.replace(/(\d)[.,\s](?=\d{3}\b)/g, '$1');
    const numbers = (numberText.match(/\d+/g) || []).map(Number);
    const has = (number) => numbers.includes(number);

    if (has(2500) && has(4000)) return '2500-4000';
    if (has(4000) && has(6000)) return '4000-6000';
    if (has(2500) && (raw.includes('<') || /\b(minder|less|onder|below|tot|up to|max)\b/.test(words))) {
      return 'under-2500';
    }
    if (has(6000) && (raw.includes('>') || /6000\s*\+/.test(numberText) || /\b(meer|more|boven|above|over)\b/.test(words))) {
      return 'over-6000';
    }
    return '';
  };

  const selectOptionMatchesExpected = (fieldName, expectedValue, option) => {
    if (!option) return false;
    const expected = normalise(expectedValue);
    if (normalise(option.value) === expected || normalise(option.textContent) === expected) return true;
    if (!isAnnualUsageField(fieldName)) return false;

    const expectedBucket = annualUsageBucket(expectedValue);
    const optionBucket = annualUsageBucket(option.textContent) || annualUsageBucket(option.value);
    return Boolean(expectedBucket && optionBucket && expectedBucket === optionBucket);
  };

  const matchingSelectOptions = (fieldName, expectedValue, options) => {
    const candidates = Array.from(options || []);
    const expected = normalise(expectedValue);
    const exact = candidates.filter((option) => {
      return normalise(option.value) === expected || normalise(option.textContent) === expected;
    });
    if (exact.length) return exact;
    if (!isAnnualUsageField(fieldName)) return [];
    return candidates.filter((option) => selectOptionMatchesExpected(fieldName, expectedValue, option));
  };

  const normaliseSemanticLabel = (value = '') => normalise(value)
    .replace(/\b(required|verplicht|vereist|optional|optioneel)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const getControlIdentifiers = (control) => {
    const identifiers = [];
    const visited = new Set();
    let current = control;

    // Shopify Forms can expose the metaobject field handle on the native
    // control, a wrapper or the web-component host. Read all of those levels
    // so a field is matched by its stable handle instead of its position.
    for (let depth = 0; current && depth < 7 && !visited.has(current); depth += 1) {
      visited.add(current);
      if (depth > 0 && (current.tagName === 'FORM' || current.matches?.(ENGINE_SELECTOR))) break;
      if (current.name) identifiers.push(String(current.name));
      if (current.id) identifiers.push(String(current.id));
      current.getAttributeNames?.().forEach((attributeName) => {
        const isIdentityAttribute = /(^|[-_:])(name|id|key|field|handle)([-_:]|$)/i.test(attributeName)
          || attributeName === 'data-testid';
        if (!isIdentityAttribute) return;
        const value = current.getAttribute(attributeName);
        if (value) identifiers.push(String(value));
      });

      const root = current.getRootNode?.();
      current = current.parentElement || (root?.host && root.host !== current ? root.host : null);
    }

    return Array.from(new Set(identifiers));
  };

  const identifierKeyScore = (identifier, targetKey) => {
    const wanted = compactNormalise(targetKey);
    const candidate = compactNormalise(identifier);
    if (!wanted || !candidate) return -1;
    if (candidate === wanted) return 500;

    const words = normalise(identifier).split(/\s+/).filter(Boolean);
    const handleWords = [...words];
    while (['field', 'custom'].includes(handleWords[0])) handleWords.shift();
    // Shopify currently renders custom field handles as IDs such as
    // `custom#first_touch_json` (and data-testid values prefixed with `field-`).
    // Match the semantic handle exactly after removing only those namespaces;
    // do not depend on a visible label for technical attribution fields.
    if (handleWords.join('') === wanted) return 490;
    if (words.includes(wanted)) return 450;
    return -1;
  };

  const collectOpenRoots = (root) => {
    const roots = [];
    const visited = new Set();

    const visit = (current) => {
      if (!current || visited.has(current)) return;
      visited.add(current);
      roots.push(current);
      const elements = current.querySelectorAll ? Array.from(current.querySelectorAll('*')) : [];
      elements.forEach((element) => {
        if (element.shadowRoot) visit(element.shadowRoot);
      });
    };

    visit(root);
    return roots;
  };

  const deepQueryAll = (root, selector) => {
    const matches = [];
    collectOpenRoots(root).forEach((current) => {
      if (current.querySelectorAll) matches.push(...current.querySelectorAll(selector));
    });
    return Array.from(new Set(matches));
  };

  const deepQuery = (root, selector) => deepQueryAll(root, selector)[0] || null;

  const labelNodeText = (node) => {
    if (!node) return '';
    const clone = node.cloneNode?.(true);
    if (!clone?.querySelectorAll) return node.textContent || '';
    clone.querySelectorAll('input, textarea, select, option, button, [role="listbox"]').forEach((element) => element.remove());
    return clone.textContent || '';
  };

  const findElementById = (root, id) => {
    if (!root || !id) return null;
    const direct = root.getElementById?.(id);
    if (direct) return direct;
    if (!root.querySelector) return null;
    try {
      const escaped = window.CSS?.escape ? window.CSS.escape(id) : id.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      return root.querySelector(`#${escaped}`);
    } catch (error) {
      return null;
    }
  };

  const findOwnLabelTexts = (control, roots) => {
    const texts = [];
    const add = (value) => {
      const text = String(value || '').trim();
      if (text) texts.push(text);
    };

    add(control.getAttribute?.('aria-label'));
    Array.from(control.labels || []).forEach((label) => add(labelNodeText(label)));

    if (control.id) {
      roots.forEach((root) => {
        if (!root.querySelector) return;
        try {
          const escaped = window.CSS?.escape ? window.CSS.escape(control.id) : control.id.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
          const label = root.querySelector(`label[for="${escaped}"]`);
          if (label) add(labelNodeText(label));
        } catch (error) {
          // Other direct label signals are sufficient.
        }
      });
    }

    const labelledBy = control.getAttribute?.('aria-labelledby');
    if (labelledBy) {
      labelledBy.split(/\s+/).filter(Boolean).forEach((id) => {
        for (const root of roots) {
          const label = findElementById(root, id);
          if (label) {
            add(labelNodeText(label));
            break;
          }
        }
      });
    }

    const wrapping = control.closest?.('label');
    if (wrapping) add(labelNodeText(wrapping));

    // Some Shopify components keep their label and native control as direct
    // children of one field wrapper. Only inspect wrappers that own at most one
    // native control; a row or form containing multiple fields is not an own
    // label boundary.
    let current = control;
    const visited = new Set();
    for (let depth = 0; current && depth < 5 && !visited.has(current); depth += 1) {
      visited.add(current);
      const root = current.getRootNode?.();
      const parent = current.parentElement || (root?.host && root.host !== current ? root.host : null);
      if (!parent || parent.tagName === 'FORM' || parent.matches?.(ENGINE_SELECTOR)) break;
      const ownedControls = parent.querySelectorAll?.('input, textarea, select')?.length || 0;
      if (ownedControls > 1) break;
      Array.from(parent.children || []).forEach((child) => {
        if (child.tagName === 'LABEL' || child.tagName === 'LEGEND') add(labelNodeText(child));
      });
      current = parent;
    }

    return Array.from(new Set(texts));
  };

  const getControlDescription = (control, roots) => {
    const ownLabelTexts = findOwnLabelTexts(control, roots);
    const parts = [
      control.name,
      control.id,
      control.type,
      control.placeholder,
      control.getAttribute('aria-label'),
      control.getAttribute('autocomplete'),
      control.getAttribute('data-testid'),
      control.getAttribute('data-field-name'),
      control.getAttribute('data-field-key'),
      ownLabelTexts.join(' ')
    ];

    let parent = control.parentElement;
    for (let depth = 0; parent && depth < 3; depth += 1) {
      parts.push(
        parent.getAttribute?.('aria-label'),
        parent.getAttribute?.('data-testid'),
        parent.getAttribute?.('data-field-name'),
        parent.getAttribute?.('data-field-key')
      );
      const directLabel = Array.from(parent.children || []).find((child) => child.tagName === 'LABEL');
      if (directLabel) parts.push(directLabel.textContent || '');
      parent = parent.parentElement;
    }

    return normalise(parts.filter(Boolean).join(' '));
  };

  const isCountryControl = (control, description) => {
    return /country|landcode|dial code|dialcode|prefix|region|calling code|country code/.test(description);
  };

  const isPhoneComponent = (control) => {
    if (!control) return false;
    const tagName = normalise(control.tagName);
    if (tagName.includes('phone field') || tagName.includes('phone input')) return true;
    try {
      return control.matches?.(PHONE_COMPONENT_SELECTOR) || false;
    } catch (error) {
      return false;
    }
  };

  const isCustomSelectButton = (control) => {
    if (!control || control.tagName !== 'BUTTON') return false;
    const type = normalise(control.type || 'button');
    return type === 'button'
      && control.hasAttribute?.('aria-expanded')
      && Boolean(control.getAttribute?.('aria-labelledby'));
  };

  const formControls = (root, options = {}) => {
    const includeHidden = Boolean(options.includeHidden);
    const roots = collectOpenRoots(root);
    return deepQueryAll(root, `input, textarea, select, button[aria-expanded][aria-labelledby], ${PHONE_COMPONENT_SELECTOR}`).filter((control) => {
      const isNativeControl = /^(INPUT|TEXTAREA|SELECT)$/.test(control.tagName);
      const isSelectButton = isCustomSelectButton(control);
      if (!isNativeControl && !isSelectButton && !('value' in control)) return false;
      const type = normalise(control.type);
      if (type === 'hidden' && !includeHidden) return false;
      if (['submit', 'reset', 'checkbox', 'radio'].includes(type)) return false;
      if (type === 'button' && !isSelectButton) return false;
      if (control.disabled) return false;
      const canUseHiddenBackingControl = includeHidden
        && (type === 'hidden' || control.tagName === 'SELECT');
      if (control.getAttribute('aria-hidden') === 'true' && !canUseHiddenBackingControl) return false;

      // A Shopify web component can expose both itself and an internal input.
      // Map only the component in that situation, otherwise the same field is
      // counted twice and subsequent fields shift one position.
      const rootNode = control.getRootNode?.();
      if (!isPhoneComponent(control) && isPhoneComponent(rootNode?.host)) return false;

      const description = getControlDescription(control, roots);
      return !isCountryControl(control, description);
    }).map((control, index) => ({
      control,
      index,
      description: getControlDescription(control, roots),
      identifiers: getControlIdentifiers(control),
      ownLabels: findOwnLabelTexts(control, roots).map(normaliseSemanticLabel).filter(Boolean)
    }));
  };

  const aliasesFor = (fieldName, customAliases = '') => {
    const key = normalise(fieldName);
    const aliases = new Set([key]);
    (FIELD_ALIASES[key] || []).forEach((alias) => aliases.add(normalise(alias)));
    String(customAliases || '').split('|').forEach((alias) => {
      if (alias.trim()) aliases.add(normalise(alias));
    });
    return Array.from(aliases).filter(Boolean);
  };

  const fieldTypeScore = (fieldName, control) => {
    const key = normalise(fieldName);
    if (key === 'email' && control.type === 'email') return 100;
    if (key === 'phone' && (control.type === 'tel' || isPhoneComponent(control))) return 140;
    if ((key === 'bericht' || key === 'situatie' || key.includes('rekenwaarden')) && control.tagName === 'TEXTAREA') return 40;
    if ((key === 'onderwerp' || key === 'jaarverbruik') && control.tagName === 'SELECT') return 40;
    if (key === 'jaarverbruik' && isCustomSelectButton(control)) return 35;
    return 0;
  };

  const findTargetControl = (field, controls, used, fallbackIndex) => {
    const aliases = aliasesFor(field.name, field.aliases);
    const fieldKey = normalise(field.name);
    // Address labels must match in full. Substring scoring gives Huisnummer
    // and Huisnummertoevoeging the same score for the house-number field.
    // Reuse the exact own-label resolver; duplicate/missing labels fail closed.
    if (['postcode', 'huisnummer', 'huisnummertoevoeging'].includes(fieldKey)) {
      return findTargetControlByLabels({ ...field, targetLabels: aliases.join('|') }, controls, used);
    }
    let best = null;
    let bestScore = -1;
    const semanticCandidates = [];

    controls.forEach((item) => {
      if (used.has(item.control) || !isCompatibleTarget(field, item.control)) return;
      let score = fieldTypeScore(field.name, item.control);
      let hasSemanticMatch = false;
      aliases.forEach((alias) => {
        if (!alias) return;
        if (item.description === alias) {
          score += 180;
          hasSemanticMatch = true;
        } else if (item.description.includes(alias)) {
          score += 90 + Math.min(alias.length, 30);
          hasSemanticMatch = true;
        }
      });
      if (score > bestScore) {
        best = item;
        bestScore = score;
      }
      if (hasSemanticMatch) semanticCandidates.push({ item, score });
    });

    const requiresSemanticMatch = fieldKey === 'jaarverbruik';
    if (best && bestScore > 0 && !requiresSemanticMatch) return best.control;
    if (requiresSemanticMatch) {
      semanticCandidates.sort((left, right) => right.score - left.score);
      if (
        semanticCandidates[0]
        && (!semanticCandidates[1] || semanticCandidates[0].score > semanticCandidates[1].score)
      ) {
        return semanticCandidates[0].item.control;
      }
      // Shopify Forms sometimes renders the visible Jaarverbruik label outside
      // the actual control, so that control has no usable semantic description.
      // In that case only accept one unambiguous unused dropdown: either a
      // native SELECT or Shopify's aria-labelled select button. Never fall back
      // by position or map Jaarverbruik onto a text/phone field.
      const uniqueUnusedDropdowns = controls.filter((item) => {
        return !used.has(item.control)
          && (item.control.tagName === 'SELECT' || isCustomSelectButton(item.control))
          && isCompatibleTarget(field, item.control);
      });
      return uniqueUnusedDropdowns.length === 1 ? uniqueUnusedDropdowns[0].control : null;
    }
    const fallback = controls[fallbackIndex];
    if (fallback && !used.has(fallback.control) && isCompatibleTarget(field, fallback.control)) {
      return fallback.control;
    }
    return controls.find((item) => !used.has(item.control) && isCompatibleTarget(field, item.control))?.control || null;
  };

  const uniqueBestControl = (candidates) => {
    if (!candidates.length) return null;
    candidates.sort((a, b) => b.score - a.score);
    if (candidates[1] && candidates[0].score === candidates[1].score) return null;
    return candidates[0].control;
  };

  const targetCompatibilityRank = (field, control) => {
    const key = normalise(field.name);
    const tagName = control.tagName;
    const type = normalise(control.type);
    const visibleInputRank = type === 'hidden' ? 10 : 30;

    if (key === 'phone') return (type === 'tel' || isPhoneComponent(control)) ? 50 : -1;
    if (key === 'email') {
      if (tagName !== 'INPUT' || !['email', 'text', 'hidden', ''].includes(type)) return -1;
      if (type === 'email') return 50;
      if (type === 'hidden') return 10;
      const emailHint = normalise([
        control.getAttribute?.('autocomplete'),
        control.getAttribute?.('inputmode'),
        control.getAttribute?.('aria-label')
      ].filter(Boolean).join(' '));
      return /email|e mail/.test(emailHint) ? 40 : 30;
    }
    if (key === 'onderwerp') {
      if (tagName === 'SELECT') return 60;
      if (tagName !== 'INPUT' || !['text', 'hidden', ''].includes(type)) return -1;
      if (type === 'hidden') return 10;
      return control.getAttribute?.('role') === 'combobox' ? 50 : 30;
    }
    if (key === 'jaarverbruik') {
      if (tagName === 'SELECT') return 60;
      return isCustomSelectButton(control) ? 55 : -1;
    }
    if (key === 'bericht' || key === 'situatie' || key.includes('rekenwaarden')) return tagName === 'TEXTAREA' ? 50 : -1;
    if (['first name', 'ordernummer', 'bronpagina', 'pagina url'].includes(key)) return tagName === 'INPUT' ? visibleInputRank : -1;

    if (field.sourceTag === 'SELECT') return tagName === 'SELECT' ? 60 : -1;
    if (field.sourceTag === 'TEXTAREA') return tagName === 'TEXTAREA' ? 50 : -1;
    if (field.sourceTag === 'INPUT' && field.sourceType !== 'hidden') return tagName === 'INPUT' ? visibleInputRank : -1;
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(tagName) || isPhoneComponent(control)) return 20;
    return -1;
  };

  const isCompatibleTarget = (field, control) => targetCompatibilityRank(field, control) >= 0;

  const bestIdentifierKeyScore = (item, targetKey) => item.identifiers.reduce((best, identifier) => {
    return Math.max(best, identifierKeyScore(identifier, targetKey));
  }, -1);

  const findTargetControlByKey = (field, controls, used) => {
    if (!field.targetKey) return null;
    const candidates = [];

    controls.forEach((item) => {
      if (used.has(item.control) || !isCompatibleTarget(field, item.control)) return;
      const keyScore = bestIdentifierKeyScore(item, field.targetKey);
      if (keyScore < 0) return;
      candidates.push({
        control: item.control,
        score: (targetCompatibilityRank(field, item.control) * 1000) + keyScore
      });
    });

    return uniqueBestControl(candidates);
  };

  const findTargetControlByLabels = (field, controls, used) => {
    const labels = String(field.targetLabels || '')
      .split('|')
      .flatMap((label) => [label, ...(FIELD_ALIASES[normalise(label)] || [])])
      .map(normaliseSemanticLabel)
      .filter(Boolean);
    if (!labels.length) return null;

    const candidates = [];
    controls.forEach((item) => {
      if (used.has(item.control) || !isCompatibleTarget(field, item.control)) return;
      // Hidden backing inputs have no trustworthy visible label. They may only
      // be selected through an exact Shopify field handle in the key resolver.
      if (normalise(item.control.type) === 'hidden') return;
      if (!labels.some((label) => item.ownLabels.includes(label))) return;

      // The exact own label and compatible control type establish semantic
      // identity. A live exact handle only breaks a tie between otherwise
      // equivalent labelled controls; it can never override the label.
      const keyScore = field.targetKey ? bestIdentifierKeyScore(item, field.targetKey) : -1;
      candidates.push({
        control: item.control,
        score: 1000000 + (targetCompatibilityRank(field, item.control) * 1000) + Math.max(0, keyScore)
      });
    });

    // Equal-scoring duplicate labels remain ambiguous and deliberately fail.
    return uniqueBestControl(candidates);
  };

  const dispatchControlEvent = (control, eventName, value = '') => {
    const view = control.ownerDocument?.defaultView || window;
    let event;

    if ((eventName === 'beforeinput' || eventName === 'input') && typeof view.InputEvent === 'function') {
      event = new view.InputEvent(eventName, {
        bubbles: true,
        composed: true,
        cancelable: eventName === 'beforeinput',
        data: value,
        inputType: value ? 'insertText' : 'deleteContentBackward'
      });
    } else {
      event = new view.Event(eventName, { bubbles: true, composed: true });
    }

    control.dispatchEvent(event);
  };

  const normalisePhoneForShopify = (value) => {
    const raw = value == null ? '' : String(value).trim();
    if (!raw) return '';

    const digits = raw.replace(/\D/g, '');

    // Shopify Forms displays the selected country code separately. When an
    // autofilled customer number already contains +31, putting the complete
    // international number in the local-number input makes the Forms phone
    // component report that the phone number is missing. Convert Dutch E.164
    // numbers back to the familiar national notation expected by that input.
    if (/^\+31/.test(raw) && digits.length > 2) return `0${digits.slice(2)}`;
    if (/^0031/.test(digits) && digits.length > 4) return `0${digits.slice(4)}`;
    if (/^31/.test(digits) && digits.length === 11) return `0${digits.slice(2)}`;

    return raw;
  };


  const phoneAsE164 = (value) => {
    const raw = value == null ? '' : String(value).trim();
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    if (raw.startsWith('+')) return `+${digits}`;
    if (digits.startsWith('0031')) return `+31${digits.slice(4)}`;
    if (digits.startsWith('31') && digits.length === 11) return `+${digits}`;
    if (digits.startsWith('0')) return `+31${digits.slice(1)}`;
    return `+31${digits}`;
  };

  const setNativeValue = (control, value, fieldName = '') => {
    if (!control) return;
    const safeValue = value == null ? '' : String(value);
    const view = control.ownerDocument?.defaultView || window;

    try {
      control.focus({ preventScroll: true });
    } catch (error) {
      control.focus?.();
    }

    if (control.tagName === 'SELECT') {
      const matches = matchingSelectOptions(fieldName, safeValue, control.options);
      const option = matches.length === 1 ? matches[0] : null;
      const prototype = view.HTMLSelectElement?.prototype;
      const setter = prototype ? Object.getOwnPropertyDescriptor(prototype, 'value')?.set : null;
      const nextValue = option ? option.value : safeValue;
      if (setter) setter.call(control, nextValue);
      else control.value = nextValue;
      dispatchControlEvent(control, 'input', nextValue);
      dispatchControlEvent(control, 'change', nextValue);
    } else if (control.tagName === 'INPUT' || control.tagName === 'TEXTAREA') {
      const prototype = control.tagName === 'TEXTAREA'
        ? view.HTMLTextAreaElement?.prototype
        : view.HTMLInputElement?.prototype;
      const setter = prototype ? Object.getOwnPropertyDescriptor(prototype, 'value')?.set : null;
      const previousValue = control.value;

      if (setter) setter.call(control, safeValue);
      else control.value = safeValue;

      // React, Preact and Web Component based fields do not all listen to the
      // same event. Dispatch the native input sequence after using the browser
      // value setter, so Shopify Forms updates its internal state as well.
      control._valueTracker?.setValue?.(previousValue);
      dispatchControlEvent(control, 'beforeinput', safeValue);
      dispatchControlEvent(control, 'input', safeValue);
      dispatchControlEvent(control, 'change', safeValue);
    } else {
      // Shopify can render phone fields as a Web Component. Setting only the
      // input in its shadow root updates what is visible, but not necessarily
      // the component state used during submission. Update the public value
      // property as well and emit the same composed event sequence.
      let prototype = Object.getPrototypeOf(control);
      let setter = null;
      while (prototype && !setter) {
        setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set || null;
        prototype = Object.getPrototypeOf(prototype);
      }
      if (setter) setter.call(control, safeValue);
      else {
        try { control.value = safeValue; } catch (error) { /* Read-only component. */ }
      }
      try { control.setAttribute('value', safeValue); } catch (error) { /* Property update is enough. */ }
      dispatchControlEvent(control, 'beforeinput', safeValue);
      dispatchControlEvent(control, 'input', safeValue);
      dispatchControlEvent(control, 'change', safeValue);
    }

    dispatchControlEvent(control, 'blur', safeValue);
  };

  const labelledElementById = (control, id) => {
    if (!control || !id) return null;
    const root = control.getRootNode?.();
    if (!root?.querySelectorAll) return null;
    return Array.from(root.querySelectorAll('[id]')).find((element) => element.id === id) || null;
  };

  const customSelectCurrentValue = (control) => {
    if (!isCustomSelectButton(control)) return '';
    const explicit = [
      control.getAttribute?.('aria-valuetext'),
      control.getAttribute?.('data-value')
    ].find((value) => String(value || '').trim());
    if (explicit) return String(explicit).trim();

    const labelledIds = String(control.getAttribute('aria-labelledby') || '')
      .split(/\s+/)
      .filter(Boolean);
    for (const id of labelledIds) {
      const element = labelledElementById(control, id);
      if (!element || element === control || !control.contains(element)) continue;
      const text = String(element.textContent || '').trim();
      if (text) return text;
    }
    return String(control.textContent || '').trim();
  };

  const controlledListboxFor = (engine, control) => {
    const controlledId = String(control.getAttribute?.('aria-controls') || '').trim();
    const listboxes = deepQueryAll(engine, '[role="listbox"]');
    if (controlledId) {
      const controlled = listboxes.find((listbox) => listbox.id === controlledId);
      if (controlled) return controlled;
    }

    const semanticMatches = listboxes.filter((listbox) => {
      const options = Array.from(listbox.querySelectorAll?.('[role="option"]') || []);
      return options.some((option) => annualUsageBucket(option.textContent));
    });
    return semanticMatches.length === 1 ? semanticMatches[0] : null;
  };

  const setCustomSelectValue = async (engine, control, field) => {
    const expected = field.value == null ? '' : String(field.value);
    const current = customSelectCurrentValue(control);
    if (
      normalise(current) === normalise(expected)
      || selectOptionMatchesExpected(field.name, expected, { value: current, textContent: current })
    ) return true;

    try { control.focus({ preventScroll: true }); } catch (error) { control.focus?.(); }
    if (control.getAttribute('aria-expanded') !== 'true') control.click?.();

    let listbox = null;
    let matches = [];
    const startedAt = Date.now();
    while (Date.now() - startedAt < 1600) {
      listbox = controlledListboxFor(engine, control);
      const options = Array.from(listbox?.querySelectorAll?.('[role="option"]') || []);
      matches = matchingSelectOptions(field.name, expected, options);
      if (matches.length === 1) break;
      await sleep(60);
    }

    if (!listbox || matches.length !== 1) {
      if (control.getAttribute('aria-expanded') === 'true') control.click?.();
      return false;
    }

    const option = matches[0];
    try { option.focus({ preventScroll: true }); } catch (error) { option.focus?.(); }
    option.click?.();
    await sleep(120);

    const selected = customSelectCurrentValue(control);
    return normalise(selected) === normalise(expected)
      || selectOptionMatchesExpected(field.name, expected, { value: selected, textContent: selected });
  };

  const scoreDutchCountryOption = (option) => {
    const value = normalise(option?.value);
    const text = normalise(option?.textContent);
    const combined = `${value} ${text}`;
    let score = 0;
    if (/netherlands|nederland/.test(combined)) score += 100;
    if (/(^| )nl( |$)/.test(combined)) score += 80;
    if (/\+?31/.test(`${option?.value || ''} ${option?.textContent || ''}`)) score += 60;
    return score;
  };

  const setDutchPhoneCountry = async (engine) => {
    const roots = collectOpenRoots(engine);
    let countryWasSet = false;

    deepQueryAll(engine, 'select').forEach((control) => {
      const description = getControlDescription(control, roots);
      if (!isCountryControl(control, description)) return;
      const option = Array.from(control.options || [])
        .map((candidate) => ({ candidate, score: scoreDutchCountryOption(candidate) }))
        .sort((a, b) => b.score - a.score)[0];
      if (!option || option.score <= 0) return;
      setNativeValue(control, option.candidate.value);
      countryWasSet = true;
    });

    deepQueryAll(engine, PHONE_COMPONENT_SELECTOR).forEach((component) => {
      const countryValues = {
        country: 'NL',
        countryCode: 'NL',
        region: 'NL',
        dialCode: '+31'
      };
      Object.entries(countryValues).forEach(([property, value]) => {
        try {
          if (property in component) component[property] = value;
        } catch (error) {
          // Not every Shopify component exposes every country property.
        }
      });
    });

    if (countryWasSet) return;

    // Some versions use a listbox instead of a native select. It is safe to
    // open this because the embedded Forms block is visually hidden.
    const countryButton = deepQueryAll(engine, 'button[aria-haspopup="listbox"], button[aria-haspopup="menu"]')
      .find((button) => isCountryControl(button, getControlDescription(button, roots)));
    if (!countryButton) return;

    countryButton.click();
    await sleep(60);
    const option = deepQueryAll(engine, '[role="option"], [role="menuitem"]')
      .map((candidate) => ({ candidate, score: scoreDutchCountryOption(candidate) }))
      .sort((a, b) => b.score - a.score)[0];
    if (option?.score > 0) option.candidate.click();
  };

  const setPhoneCompanionValues = (engine, value) => {
    const e164 = phoneAsE164(value);
    if (!e164) return;
    const roots = collectOpenRoots(engine);

    deepQueryAll(engine, 'input[type="hidden"]').forEach((control) => {
      const description = getControlDescription(control, roots);
      if (!aliasesFor('Phone').some((alias) => alias && description.includes(alias))) return;
      setNativeValue(control, e164);
    });
  };

  const setPhoneValue = async (engine, target, rawValue, format = 'international') => {
    const national = normalisePhoneForShopify(rawValue);
    const e164 = phoneAsE164(rawValue);
    const visibleValue = format === 'international' ? e164 : national;

    await setDutchPhoneCountry(engine);

    // Update the mapped control first. Then synchronise any public phone
    // component and its internal tel field, covering both current and older
    // Shopify Forms implementations.
    setNativeValue(target, visibleValue);
    deepQueryAll(engine, PHONE_COMPONENT_SELECTOR).forEach((component) => {
      setNativeValue(component, e164 || visibleValue);
    });
    deepQueryAll(engine, 'input[type="tel"]').forEach((control) => {
      if (control !== target) setNativeValue(control, visibleValue);
    });
    setPhoneCompanionValues(engine, e164 || rawValue);
  };

  const findEmbeddedForm = (engine) => {
    for (const root of collectOpenRoots(engine)) {
      const form = root.querySelector?.('form');
      if (form) return { engine, form };
    }
    return null;
  };

  const readEmbeddedFormId = (engine) => {
    const candidates = [engine, ...deepQueryAll(engine, '[data-forms-id], [id*="forms-root-"]')];
    for (const candidate of candidates) {
      const identity = [
        candidate?.getAttribute?.('data-forms-id'),
        candidate?.id
      ].filter(Boolean).join(' ');
      const match = identity.match(/forms-root-(\d+)/i);
      if (match) return match[1];
    }
    return '';
  };

  const scoreEngineForForm = (formId, engine) => {
    const expected = FORM_ORDERS[String(formId)] || [];
    const controls = formControls(engine);
    if (!controls.length) return -1;

    let labelMatches = 0;
    expected.forEach((fieldName) => {
      const aliases = aliasesFor(fieldName);
      if (controls.some((item) => aliases.some((alias) => alias && item.description.includes(alias)))) {
        labelMatches += 1;
      }
    });

    const countDistance = Math.abs(controls.length - expected.length);
    const exactCountBonus = countDistance === 0 ? 500 : 0;
    return exactCountBonus + (labelMatches * 40) - (countDistance * 25);
  };

  const waitForEmbeddedForm = async (formId) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < WAIT_FOR_FORM_MS) {
      const safeFormId = window.CSS?.escape ? window.CSS.escape(String(formId)) : String(formId).replace(/[^0-9A-Za-z_-]/g, '');
      const engines = Array.from(document.querySelectorAll(`${ENGINE_SELECTOR}[data-zv-form-id="${safeFormId}"]`));
      for (const engine of engines) {
        const embedded = findEmbeddedForm(engine);
        const embeddedFormId = readEmbeddedFormId(engine);
        if (embedded && (!embeddedFormId || embeddedFormId === String(formId))) return embedded;
      }

      // Shopify app blocks are opaque to their parent section. On some uploaded themes
      // the app form renders correctly, while its form_id isn't exposed in block.settings.
      // Identify the correct engine from its unique field signature as a safe fallback.
      const candidates = Array.from(document.querySelectorAll('[data-zv-shopify-engine]'))
        .map((engine) => ({
          engine,
          embedded: findEmbeddedForm(engine),
          embeddedFormId: readEmbeddedFormId(engine),
          score: scoreEngineForForm(formId, engine)
        }))
        .filter((item) => {
          const identityMatches = !item.embeddedFormId || item.embeddedFormId === String(formId);
          return item.embedded && identityMatches && item.score >= 0;
        })
        .sort((a, b) => b.score - a.score);

      if (candidates.length) {
        const best = candidates[0];
        const second = candidates[1];
        if (best.score >= 300 && (!second || best.score > second.score)) {
          best.engine.dataset.zvFormId = String(formId);
          return best.embedded;
        }
      }

      await sleep(120);
    }
    return null;
  };

  const removeHiddenFormFromTabOrder = (engine) => {
    // inert covers current and later-hydrated controls, including shadow DOM.
    // Native requestSubmit and programmatic field mapping remain available.
    engine.setAttribute('inert', '');
    engine.setAttribute('aria-hidden', 'true');
    deepQueryAll(engine, 'a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  };

  const readSubmissionState = (embedded) => {
    // Verified against Shopify Forms forms-2553 on 2026-09-08. AppRouter sets
    // data-current-step="success"; AppSuccess renders banner-success. A spinner
    // or an unrelated role=status, translated copy, or replaced form is NOT a
    // receipt. If this external contract changes, time out without tracking.
    const success = Boolean(deepQuery(embedded.engine, '#app-embed[data-current-step="success"]')
      && deepQuery(embedded.engine, '[data-testid="banner-success"]'));
    const errorNodes = deepQueryAll(embedded.engine, '[data-testid="banner-warning"], [role="alert"], [data-testid*="error"], [class*="error"]');
    const invalidControls = deepQueryAll(embedded.engine, '[aria-invalid="true"]');
    return {
      success,
      engineConnected: Boolean(embedded.engine?.isConnected),
      errors: errorNodes.map((node) => node.textContent?.trim()).filter(Boolean),
      invalidCount: invalidControls.length
    };
  };

  const pendingStatusTargets = new WeakMap;

const showPendingSubmissionStatus = (sourceForm, formId, phase) => {
        const english = String(document.documentElement?.lang || "nl").toLowerCase().startsWith("en");
        const message = phase === "unconfirmed"
            ? (english ? "We cannot confirm receipt yet. Please do not submit again. Email info@zinvolt.com so we can check your request." : "We kunnen de ontvangst nog niet bevestigen. Verstuur je aanvraag niet opnieuw. Mail info@zinvolt.com, dan controleren we je aanvraag.")
            : (english ? "Confirmation is taking a little longer. Please do not submit again." : "De bevestiging duurt wat langer. Verstuur je aanvraag niet opnieuw.");
        if (sourceForm) {
            let record = pendingStatusTargets.get(sourceForm);
            if (!record) {
                const selector = sourceForm.dataset.zvErrorTarget;
                let target = selector ? sourceForm.closest("[data-zv-form-scope]")?.querySelector(selector) || document.querySelector(selector)
                    : sourceForm.querySelector('[data-zv-form-error]');
                const created = !target;
                if (!target) {
                    target = document.createElement("div");
                    target.className = "zv-shopify-form-pending-status";
                    sourceForm.prepend(target);
                }
                record = { target, created, role: target.getAttribute("role"), live: target.getAttribute("aria-live"), style: target.getAttribute("style") };
                pendingStatusTargets.set(sourceForm, record);
                target.setAttribute("role", "status");
                target.setAttribute("aria-live", "polite");
                Object.assign(target.style, { background: "#f3f6ef", color: "#42503f", border: "1px solid #dce4d6" });
                if (created) Object.assign(target.style, { padding: "12px 16px", borderRadius: "12px", marginBottom: "16px" });
            }
            record.target.textContent = message;
            if (phase === "unconfirmed") {
                const email = "info@zinvolt.com";
                const [before, after = ""] = message.split(email);
                const link = document.createElement("a");
                link.href = `mailto:${email}`;
                link.textContent = email;
                link.style.color = "inherit";
                link.style.textDecoration = "underline";
                record.target.replaceChildren(document.createTextNode(before), link, document.createTextNode(after));
            }
            record.target.hidden = false;
        }
        try { document.dispatchEvent(new CustomEvent("zv:shopify-form-pending", { detail: { formId, form: sourceForm, phase, message } })); } catch {}
    };

const clearPendingSubmissionStatus = sourceForm => {
        if (!sourceForm) return;
        const record = pendingStatusTargets.get(sourceForm);
        if (!record) return;
        pendingStatusTargets.delete(sourceForm);
        if (record.created) { record.target.remove(); return; }
        record.target.textContent = "";
        record.target.hidden = true;
        for (const [attribute, previous] of [["role", record.role], ["aria-live", record.live], ["style", record.style]]) {
            if (previous === null) record.target.removeAttribute(attribute); else record.target.setAttribute(attribute, previous);
        }
    };

const waitForSubmissionResult = (embedded, baseline = readSubmissionState(embedded), context = {}) => new Promise((resolve, reject) => {
        const startedAt = Date.now();
        let settled = false, slowReported = false, unconfirmedReported = false, interval = null;
        let observed = embedded, observedBaseline = baseline;
        const knownEngines = new Set(Array.from(document.querySelectorAll?.(ENGINE_SELECTOR) || []));
        knownEngines.add(embedded.engine);
        const stopPolling = () => { if (interval !== null) window.clearInterval(interval); interval = null; };
        const finish = (callback, value) => {
            if (settled) return;
            settled = true;
            stopPolling();
            window.removeEventListener("pagehide", stopPolling);
            window.removeEventListener("pageshow", resume);
            context.signal?.removeEventListener("abort", abort);
            clearPendingSubmissionStatus(context.sourceForm);
            callback(value);
        };
        const check = () => {
            let state = readSubmissionState(observed);
            if (!state.engineConnected && /^[0-9]+$/.test(String(context.formId || ""))) {
                const replacements = Array.from(document.querySelectorAll?.(ENGINE_SELECTOR) || []).filter(engine =>
                    engine.isConnected && !knownEngines.has(engine) && String(engine.dataset.zvFormId || "") === String(context.formId)
                    && readEmbeddedFormId(engine) === String(context.formId));
                if (replacements.length === 1) {
                    const replacement = { engine: replacements[0], form: findEmbeddedForm(replacements[0])?.form || null };
                    const replacementBaseline = readSubmissionState(replacement);
                    if (!replacementBaseline.success) {
                        knownEngines.add(replacement.engine);
                        observed = replacement;
                        observedBaseline = { ...baseline, success: false };
                        state = replacementBaseline;
                    }
                }
            }
            const newErrors = state.errors.filter(message => !observedBaseline.errors.includes(message));
            if (state.invalidCount > observedBaseline.invalidCount || newErrors.length) {
                finish(reject, new Error(newErrors[0] || window.ZinVoltI18n.t("check_the_details_you_entered_f02286")));
                return;
            }
            if (state.engineConnected && !observedBaseline.success && state.success) {
                finish(resolve, true);
                return;
            }
            const elapsed = Date.now() - startedAt;
            if (!slowReported && !unconfirmedReported && elapsed >= WAIT_FOR_SUCCESS_MS) {
                slowReported = true;
                showPendingSubmissionStatus(context.sourceForm, context.formId, "waiting");
            }
            if (!unconfirmedReported && (elapsed >= 90000 || !state.engineConnected)) {
                unconfirmedReported = true;
                showPendingSubmissionStatus(context.sourceForm, context.formId, "unconfirmed");
            }
        };
        const abort = () => finish(reject, context.signal?.reason || new Error("Form submission could not be started."));
        const resume = () => {
            if (settled || interval !== null) return;
            interval = window.setInterval(check, 250);
            check();
        };
        window.addEventListener("pagehide", stopPolling);
        window.addEventListener("pageshow", resume);
        context.signal?.addEventListener("abort", abort, { once: true });
        if (context.signal?.aborted) abort(); else resume();
    });

  const readSourceFields = (form) => Array.from(form.querySelectorAll('[data-zv-field]')).map((control) => ({
    name: control.dataset.zvField,
    aliases: control.dataset.zvFieldAliases || '',
    targetKey: control.dataset.zvTargetKey || '',
    targetLabels: control.dataset.zvTargetLabels || '',
    targetOptional: control.dataset.zvTargetOptional === 'true',
    sourceTag: control.tagName,
    sourceType: normalise(control.type),
    value: control.type === 'checkbox' ? (control.checked ? control.value || 'Ja' : 'Nee') : control.value
  }));

  const normaliseFieldInput = (fields) => {
    if (Array.isArray(fields)) {
      return fields.map((field) => ({
        ...field,
        aliases: field.aliases || '',
        targetKey: field.targetKey || '',
        targetLabels: field.targetLabels || '',
        targetOptional: field.targetOptional === true,
        sourceTag: field.sourceTag || '',
        sourceType: field.sourceType || ''
      }));
    }
    return Object.entries(fields || {}).map(([name, value]) => ({
      name,
      value,
      aliases: '',
      targetKey: '',
      targetLabels: '',
      targetOptional: false,
      sourceTag: '',
      sourceType: ''
    }));
  };

  const resolveFieldMapping = (embedded, orderedFields, strictMode) => {
    const controls = formControls(embedded.engine);
    // Explicit field handles may point to native hidden inputs. Keep those
    // available for strict key matching, while legacy positional mapping only
    // sees visible controls and therefore cannot shift onto Shopify internals.
    const strictControls = formControls(embedded.engine, { includeHidden: true });
    const used = new Set();
    const mapped = [];
    const missingStrictFields = [];
    const unmappedSourceFields = [];

    orderedFields.forEach((field, index) => {
      if (field.targetKey || field.targetLabels) {
        const target = field.preferTargetKey
          ? (findTargetControlByKey(field, strictControls, used) || findTargetControlByLabels(field, strictControls, used))
          : (findTargetControlByLabels(field, strictControls, used) || findTargetControlByKey(field, strictControls, used));
        if (!target) {
          if (!field.targetOptional) missingStrictFields.push(field.targetKey || field.name);
          return;
        }
        used.add(target);
        mapped.push({ field, target });
        return;
      }

      // Once a form opts into fixed handles, every required source field must
      // have one. Falling back to DOM position for a single missing key would
      // move that field and every field after it into the wrong metaobject key.
      if (strictMode) {
        if (!field.targetOptional) missingStrictFields.push(field.name);
        return;
      }

      // Shopify can render Jaarverbruik as an aria-hidden native SELECT or as
      // an aria-labelled button/listbox component. Resolve that one semantic
      // field against the complete dropdown set. Other legacy fields remain
      // limited to visible controls so they cannot shift onto hidden internals.
      const candidateControls = normalise(field.name) === 'jaarverbruik'
        ? strictControls
        : controls;
      const target = findTargetControl(field, candidateControls, used, index);
      if (!target) {
        if (!field.targetOptional) unmappedSourceFields.push(field.name);
        return;
      }
      used.add(target);
      mapped.push({ field, target });
    });

    const requiredUnmapped = controls.filter(({ control }) => control.required && !used.has(control));
    return { controls, mapped, missingStrictFields, unmappedSourceFields, requiredUnmapped };
  };

  const selectContainsExpectedOption = (field, target) => {
    if (target.tagName !== 'SELECT') return true;
    return matchingSelectOptions(field.name, field.value, target.options).length === 1;
  };

  const waitForStableFieldMapping = async (embedded, orderedFields, strictMode) => {
    const startedAt = Date.now();
    let previousTargets = [];
    let lastResult = resolveFieldMapping(embedded, orderedFields, strictMode);

    while (Date.now() - startedAt < WAIT_FOR_MAPPING_MS) {
      const result = resolveFieldMapping(embedded, orderedFields, strictMode);
      lastResult = result;
      const optionsReady = result.mapped.every(({ field, target }) => selectContainsExpectedOption(field, target));
      const ready = result.missingStrictFields.length === 0
        && result.unmappedSourceFields.length === 0
        && result.requiredUnmapped.length === 0
        && optionsReady;
      const targets = result.mapped.map(({ target }) => target);
      const sameTargets = ready
        && targets.length === previousTargets.length
        && targets.every((target, index) => target === previousTargets[index] && target.isConnected);

      if (sameTargets) return { ...result, stable: true };
      previousTargets = ready ? targets : [];
      await sleep(120);
    }

    return { ...lastResult, stable: false };
  };

  const controlContainsExpectedValue = (field, target) => {
    if (!target?.isConnected) return false;
    if (normalise(field.name) === 'phone') return true;

    const expected = field.value == null ? '' : String(field.value);
    if (isCustomSelectButton(target)) {
      const selected = customSelectCurrentValue(target);
      return normalise(selected) === normalise(expected)
        || selectOptionMatchesExpected(field.name, expected, { value: selected, textContent: selected });
    }
    const current = target.value == null ? target.getAttribute?.('value') || '' : String(target.value);
    if (target.tagName === 'SELECT') {
      const selectedOption = target.options?.[target.selectedIndex];
      return normalise(current) === normalise(expected)
        || selectOptionMatchesExpected(field.name, expected, selectedOption);
    }
    return current === expected;
  };

  const writeMappedValues = async (embedded, mapped, phoneFormat = 'international') => {
    let phoneMapping = null;
    for (const { field, target } of mapped) {
      const isPhone = normalise(field.name) === 'phone';
      if (isPhone) {
        phoneMapping = { field, target };
        await setPhoneValue(embedded.engine, target, field.value, phoneFormat);
      } else if (isCustomSelectButton(target)) {
        await setCustomSelectValue(embedded.engine, target, field);
      } else {
        setNativeValue(target, field.value, field.name);
      }
    }
    return phoneMapping;
  };

  const nowMs = () => Date.now();

  const duplicateStorageKey = (formId) => `${DUPLICATE_STORAGE_PREFIX}${String(formId || '').trim()}`;

  const readRecentSuccessAt = (formId) => {
    if (!formId) return 0;
    try {
      const value = Number(window.localStorage?.getItem(duplicateStorageKey(formId)) || 0);
      return Number.isFinite(value) ? value : 0;
    } catch (error) {
      return 0;
    }
  };

  const markRecentSuccess = (formId) => {
    if (!formId) return;
    const value = String(nowMs());
    try {
      window.localStorage?.setItem(duplicateStorageKey(formId), value);
    } catch (error) {
      // Storage is a convenience guard only; form submission must still work.
    }
  };

  const recentSuccessRemainingMs = (formId) => {
    const lastSuccessAt = readRecentSuccessAt(formId);
    if (!lastSuccessAt) return 0;
    const remaining = DUPLICATE_SUBMIT_COOLDOWN_MS - (nowMs() - lastSuccessAt);
    return remaining > 0 ? remaining : 0;
  };

  const duplicateSubmissionMessage = (remainingMs) => {
    const seconds = Math.max(1, Math.ceil(Number(remainingMs || 0) / 1000));
    return window.ZinVoltI18n.t("your_request_has_already_been_received_wait_57b56b", {seconds});
  };

  const setBusyForFormId = (formId, busy) => {
    if (!formId) return;
    document.querySelectorAll?.(SOURCE_SELECTOR).forEach((form) => {
      if (String(form.dataset.zvFormId || '') !== String(formId)) return;
      setBusy(form, busy);
    });
  };

  const submitOnce = async (source, { previewCheck = false } = {}) => {
    const sourceForm = source instanceof HTMLFormElement ? source : source?.form || null;
    const tracking = createTrackingContext(
      sourceForm?.dataset.zvFormId || source?.formId,
      sourceForm?.dataset.zvFormName || source?.formName
    );
    const { formId, formName } = tracking;
    if (!formId) throw new Error(window.ZinVoltI18n.t("the_form_id_is_missing_1ebc3a"));

    let embedded = await waitForEmbeddedForm(formId);
    if (!embedded) throw new Error(window.ZinVoltI18n.t("the_form_could_not_be_loaded_refresh_7044ab"));
    removeHiddenFormFromTabOrder(embedded.engine);

    const sourceFields = sourceForm ? readSourceFields(sourceForm) : normaliseFieldInput(source?.fields);
    // Strict mode is determined exclusively by the real ZinVolt source fields.
    // The technical Event ID field uses a strict target locator, but must never
    // force legacy forms into strict mode or make tracking more important than
    // a successful customer submission.
    const strictMode = sourceFields.some((field) => Boolean(field.targetKey || field.targetLabels));
    const eventIdField = createTechnicalEventIdField(formId, tracking.eventId);
    if (EVENT_ID_FORMS.has(String(formId)) && !isValidEventId(tracking.eventId, formId)) {
      reportTrackingWarning(formId, 'event_id_invalid');
    }
    const metaSignalFields = createTechnicalMetaSignalFields(formId, tracking);
    const attributionFields = createTechnicalAttributionFields(formId, tracking.leadContext);
    const technicalTrackingFields = [eventIdField, ...metaSignalFields, ...attributionFields].filter(Boolean);
    const mappingFields = [...sourceFields, ...technicalTrackingFields];
    const byName = new Map(mappingFields.map((field) => [normalise(field.name), field]));
    const configuredOrder = (FORM_ORDERS[formId] || mappingFields.map((field) => field.name))
      .filter((name) => !TECHNICAL_TRACKING_FIELD_NAMES.has(normalise(name)) || byName.has(normalise(name)));
    const orderedFields = configuredOrder.map((name) => byName.get(normalise(name)) || {
      name,
      value: '',
      aliases: '',
      targetKey: '',
      targetLabels: '',
      targetOptional: false,
      sourceTag: '',
      sourceType: ''
    });
    mappingFields.forEach((field) => {
      if (!orderedFields.some((candidate) => normalise(candidate.name) === normalise(field.name))) orderedFields.push(field);
    });
    let mapping = await waitForStableFieldMapping(embedded, orderedFields, strictMode);
    const { missingStrictFields, unmappedSourceFields, requiredUnmapped } = mapping;

    if (missingStrictFields.length) {
      throw new Error(
        window.ZinVoltI18n.t("shopify_forms_is_missing_the_fixed_field_ea5865", {fields:missingStrictFields.join(', '),form:formId})
      );
    }

    if (unmappedSourceFields.length) {
      throw new Error(
        window.ZinVoltI18n.t("shopify_forms_is_missing_fields_in_form_290b5a", {fields:unmappedSourceFields.join(', '),form:formId})
      );
    }

    if (requiredUnmapped.length) {
      const description = requiredUnmapped[0].description || window.ZinVoltI18n.t("an_additional_field_33560a");
      throw new Error(window.ZinVoltI18n.t("shopify_forms_still_requires_field_check_the_1e5806", {field:description,form:formId}));
    }

    if (!mapping.stable) {
      throw new Error(window.ZinVoltI18n.t("shopify_forms_is_still_loading_the_fields_f10378", {form:formId}));
    }

    let readbackFailures = [];
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (!embedded.engine.isConnected) {
        embedded = await waitForEmbeddedForm(formId);
        if (!embedded) break;
      }

      mapping = await waitForStableFieldMapping(embedded, orderedFields, strictMode);
      if (
        mapping.missingStrictFields.length
        || mapping.unmappedSourceFields.length
        || mapping.requiredUnmapped.length
        || !mapping.stable
      ) break;
      removeHiddenFormFromTabOrder(embedded.engine);
      // Shopify's phone component owns both the +31 country selector and the
      // formatted phone value. Feeding the complete E.164 number lets that
      // component update its internal required-field state on the first (and
      // only) submission. A national value looked filled in our source form,
      // but Shopify still reported the hidden Phone field as empty.
      await writeMappedValues(embedded, mapping.mapped, 'international');
      await sleep(420);
      readbackFailures = mapping.mapped.filter(({ field, target }) => !controlContainsExpectedValue(field, target));
      if (!readbackFailures.length) break;
    }

    if (!embedded) {
      throw new Error(window.ZinVoltI18n.t("the_form_could_not_be_loaded_refresh_7044ab"));
    }

    // Check once more immediately before locating the submit button. Shopify
    // Forms may replace a hydrated field node after an input/change event.
    readbackFailures = mapping.mapped.filter(({ field, target }) => !controlContainsExpectedValue(field, target));
    const eventIdMapping = eventIdField
      ? mapping.mapped.find(({ field }) => isTechnicalEventIdField(field))
      : null;
    const eventIdReadbackFailed = readbackFailures.some(({ field }) => isTechnicalEventIdField(field));
    const consentSnapshotField = technicalTrackingFields.find(isTechnicalConsentSnapshotField) || null;
    const consentSnapshotMapping = consentSnapshotField
      ? mapping.mapped.find(({ field }) => isTechnicalConsentSnapshotField(field))
      : null;
    const consentSnapshotReadbackFailed = readbackFailures.some(({ field }) => isTechnicalConsentSnapshotField(field));
    const blockingReadbackFailures = readbackFailures.filter(({ field }) => !isTechnicalTrackingField(field));

    // Meta browser dispatch is allowed only when the exact Event ID and the
    // exact v1.1 consent snapshot were both present in Shopify's technical
    // field mapping and read back byte-for-byte immediately before submit.
    // Failure remains tracking-only: the operational customer lead continues.
    tracking.eventIdReadbackConfirmed = Boolean(
      eventIdField
      && eventIdMapping
      && !eventIdReadbackFailed
      && isValidEventId(tracking.eventId, formId)
    );
    tracking.consentSnapshotReadbackConfirmed = Boolean(
      consentSnapshotField
      && consentSnapshotMapping
      && !consentSnapshotReadbackFailed
    );

    if (eventIdField && !eventIdMapping) {
      reportTrackingWarning(formId, 'event_id_field_missing');
    } else if (eventIdMapping && eventIdReadbackFailed) {
      reportTrackingWarning(formId, 'event_id_write_failed');
    }

    technicalTrackingFields
      .filter((field) => !isTechnicalEventIdField(field))
      .forEach((field) => {
        const mappedField = mapping.mapped.find(({ field: candidate }) => candidate === field);
        const failed = readbackFailures.some(({ field: candidate }) => candidate === field);
        if (!mappedField) reportTrackingWarning(formId, `${field.technical}_field_missing`);
        else if (failed) reportTrackingWarning(formId, `${field.technical}_write_failed`);
      });

    if (mapping.missingStrictFields.length) {
      throw new Error(
        window.ZinVoltI18n.t("shopify_forms_is_missing_the_fixed_field_ea5865", {fields:mapping.missingStrictFields.join(', '),form:formId})
      );
    }
    if (mapping.unmappedSourceFields.length) {
      throw new Error(
        window.ZinVoltI18n.t("shopify_forms_is_missing_fields_in_form_290b5a", {fields:mapping.unmappedSourceFields.join(', '),form:formId})
      );
    }
    if (mapping.requiredUnmapped.length) {
      const description = mapping.requiredUnmapped[0].description || window.ZinVoltI18n.t("an_additional_field_33560a");
      throw new Error(window.ZinVoltI18n.t("shopify_forms_still_requires_field_check_the_1e5806", {field:description,form:formId}));
    }
    if (!mapping.stable || blockingReadbackFailures.length) {
      const fieldNames = blockingReadbackFailures.map(({ field }) => field.name).join(', ');
      throw new Error(window.ZinVoltI18n.t("shopify_forms_could_not_fill_in_fields_55b16b", {fields:fieldNames || window.ZinVoltI18n.t("the_fields_552a0f")}));
    }

    embedded = findEmbeddedForm(embedded.engine) || embedded;

    const invalid = mapping.controls.filter(({ control }) => {
      return typeof control.checkValidity === 'function' && !control.checkValidity();
    });
    if (invalid.length) {
      const item = invalid[0];
      throw new Error(window.ZinVoltI18n.t("check_field_46208d", {field:item.description || window.ZinVoltI18n.t("one_of_the_fields_you_entered_96625c")}));
    }

    const submitButton = deepQuery(embedded.engine, 'button[type="submit"], input[type="submit"]');
    if (!submitButton || submitButton.disabled) throw new Error(window.ZinVoltI18n.t("shopify_forms_is_not_ready_to_submit_4c7caa"));

    // A draft/design/non-production form submission would create a real
    // Shopify metaobject and therefore trigger production Flows. The exact
    // operational mapping, readback and validation above may be checked, but
    // stop here before native submit, receipt, persistence or conversion code.
    // An unknown theme role remains operationally
    // allowed but Meta-fail-closed; release preflight must prove role=main.
    const runtimeBlockReason = META_LEAD_POLICY?.runtimeBlockReason?.() || 'meta_policy_unavailable';
    if (isExplicitPreviewRuntime(runtimeBlockReason)) {
      // External consumers may treat any resolved submit() as a real receipt.
      // Only an explicit check-aware caller may receive this distinct result.
      if (previewCheck === true) return { previewOnly: true, formId, formName };
      throw new Error(window.ZinVoltI18n.t("this_is_a_theme_preview_form_submissions_524e0c"));
    }

    const sendEmbeddedForm = async () => {
      const baseline = readSubmissionState(embedded);
      if (baseline.success) {
        throw new Error(window.ZinVoltI18n.t("this_form_already_shows_a_confirmation_refresh_62e0a0"));
      }
      const submitController = new AbortController();
      const resultPromise = waitForSubmissionResult(embedded, baseline, { sourceForm, formId, signal: submitController.signal });
      try {
        if (typeof embedded.form.requestSubmit === 'function') embedded.form.requestSubmit(submitButton);
        else submitButton.click();
      } catch (error) { submitController.abort(error); }
      await resultPromise;
    };

    // One user action may create at most one Shopify submission. Earlier
    // versions retried automatically with another phone representation after
    // a phone-related response. Shopify can already have accepted that first
    // request, which creates two metaobject entries. Surface any real error to
    // the visitor instead of ever issuing a second submission automatically.
    await sendEmbeddedForm();
    tracking.submittedAt = new Date().toISOString();
    markRecentSuccess(formId);
    const canonicalLeadUuid = tracking.leadContext?.lead_uuid
      || tracking.leadContext?.submission_uuid
      || tracking.leadContext?.provisional_lead_uuid
      || '';
    if (canonicalLeadUuid) {
      try {
        window.ZinVoltLeadAttribution?.markSubmissionSuccess?.(formId, canonicalLeadUuid);
      } catch (error) {
        reportTrackingWarning(formId, 'attribution_success_cleanup_failed');
      }
    }

    const leadDefinition = LEAD_EVENT_CONFIG[formId];
    const consent = tracking.leadContext?.consent_snapshot || {};
    const trackingEvent = leadDefinition ? {
      // Preserve the already-live canonical event name; enrich its payload.
      event: 'zv_shopify_form_submitted',
      event_schema_version: '1.0',
      event_name: tracking.eventName || '',
      event_id: tracking.eventIdReadbackConfirmed ? tracking.eventId : '',
      // This source UUID is adopted as the canonical Salesdock opportunity
      // identity. Shopify system.id remains a separate source-record ID.
      submission_uuid: canonicalLeadUuid,
      lead_uuid: canonicalLeadUuid,
      lead_uuid_status: canonicalLeadUuid ? 'canonical' : 'unavailable',
      form_id: formId,
      form_name: formName,
      lead_type: leadDefinition.leadType,
      lead_stage: 'raw_submission',
      qualification_status: 'not_evaluated',
      conversion_route: leadDefinition.conversionRoute,
      // Eligibility applies only to the raw website-lead conversion. A
      // qualified lead requires separate server-side scoring plus sales
      // confirmation/override and is never inferred by this theme.
      ads_optimization_eligible: leadDefinition.adsOptimizationEligible,
      event_source_url: tracking.eventSourceUrl,
      consent_ad_user_data: consent.ad_user_data || 'denied',
      consent_ad_personalization: consent.ad_personalization || 'denied'
    } : {
      // Contact/newsletter forms keep the existing event and payload keys.
      event: 'zv_shopify_form_submitted',
      event_schema_version: '1.0',
      event_name: tracking.eventName || '',
      form_id: formId,
      form_name: formName,
      event_id: tracking.eventIdReadbackConfirmed ? tracking.eventId : '',
      event_source_url: tracking.eventSourceUrl
    };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(trackingEvent);
    } catch (error) {
      // Tracking can fail, but a confirmed Shopify submission remains success.
      reportTrackingWarning(formId, 'canonical_event_dispatch_failed');
    }

    // Send one browser Lead to the exact Meta Pixel used by the CAPI dataset.
    // The eventID is byte-for-byte the same ID persisted in Shopify Forms and
    // forwarded by Flow to CAPI, allowing Meta to deduplicate browser/server.
    // Browser tracking is best-effort and may never block the customer submit.
    dispatchMetaBrowserLead(tracking);

    const detail = {
      formId,
      formName,
      eventName: tracking.eventName,
      eventId: tracking.eventIdReadbackConfirmed ? tracking.eventId : '',
      submissionUuid: canonicalLeadUuid,
      leadUuidStatus: canonicalLeadUuid ? 'canonical' : 'unavailable',
      eventSourceUrl: tracking.eventSourceUrl,
      form: sourceForm,
      fields: sourceFields
    };
    try {
      document.dispatchEvent(new CustomEvent('zv:shopify-form-success', { detail }));
    } catch (error) {
      reportTrackingWarning(formId, 'success_dom_event_dispatch_failed');
    }
    return detail;
  };

  const inFlightSubmissions = new WeakMap();
  const inFlightByFormId = new Map();

  const submit = (source, { previewCheck = false } = {}) => {
    const sourceForm = source instanceof HTMLFormElement ? source : source?.form || null;
    const formId = String(sourceForm?.dataset.zvFormId || source?.formId || '').trim();
    const cooldownRemaining = recentSuccessRemainingMs(formId);
    if (cooldownRemaining) {
      return Promise.reject(new Error(duplicateSubmissionMessage(cooldownRemaining)));
    }

    // A single Shopify form ID may be rendered more than once on a page
    // (for example a popup plus an inline variant). Lock by form ID as well as
    // by DOM form so two UI instances cannot create two metaobject entries.
    const existingByFormId = formId ? inFlightByFormId.get(formId) : null;
    if (existingByFormId) return existingByFormId;

    const lockTarget = sourceForm || (source && typeof source === 'object' ? source : null);
    const existing = lockTarget ? inFlightSubmissions.get(lockTarget) : null;
    if (existing) return existing;

    const task = submitOnce(source, { previewCheck });
    if (formId) inFlightByFormId.set(formId, task);
    if (lockTarget) inFlightSubmissions.set(lockTarget, task);

    const clearLock = () => {
      if (formId && inFlightByFormId.get(formId) === task) inFlightByFormId.delete(formId);
      if (lockTarget && inFlightSubmissions.get(lockTarget) === task) inFlightSubmissions.delete(lockTarget);
    };
    task.then(clearLock, clearLock);
    return task;
  };

  const setBusy = (form, busy) => {
    form.dataset.zvFormBusy = busy ? 'true' : 'false';
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
    const button = form.querySelector('[type="submit"]');
    if (!button) return;
    const label = button.querySelector('[data-zv-button-label]') || button.querySelector('span') || button;
    if (!button.dataset.zvOriginalLabel) button.dataset.zvOriginalLabel = label.textContent || '';
    button.disabled = busy;
    if (busy) label.textContent = form.dataset.zvBusyLabel || window.ZinVoltI18n.t("sending_b6ad81");
    else label.textContent = button.dataset.zvOriginalLabel;
  };

  const showError = (form, message) => {
    const selector = form.dataset.zvErrorTarget;
    let target = selector ? form.closest('[data-zv-form-scope]')?.querySelector(selector) || document.querySelector(selector) : null;
    if (!target) {
      target = form.querySelector('[data-zv-form-error]');
    }
    if (!target) {
      target = document.createElement('div');
      target.dataset.zvFormError = 'true';
      target.className = 'zv-shopify-form-error';
      target.setAttribute('role', 'alert');
      form.prepend(target);
    }
    target.textContent = message;
    target.hidden = false;
    target.focus?.();
  };

  const clearError = (form) => {
    const selector = form.dataset.zvErrorTarget;
    const target = selector ? form.closest('[data-zv-form-scope]')?.querySelector(selector) || document.querySelector(selector) : form.querySelector('[data-zv-form-error]');
    if (!target) return;
    target.hidden = true;
    target.textContent = '';
  };

  const clearPreviewStatus = (form) => {
    const target = form.querySelector('[data-zv-form-preview-status]');
    if (target) target.remove();
  };

  const showPreviewStatus = (form) => {
    clearPreviewStatus(form);
    const target = document.createElement('div');
    target.dataset.zvFormPreviewStatus = 'true';
    target.className = 'zv-shopify-form-preview-status';
    target.setAttribute('role', 'status');
    target.setAttribute('tabindex', '-1');
    target.textContent = previewCheckMessage();
    form.prepend(target);
    try { target.focus({ preventScroll: true }); } catch (error) { /* Status remains readable without focus support. */ }
  };

  const showDefaultSuccess = (form) => {
    if (form.hasAttribute('data-zv-success-event-only')) return;

    const template = form.parentElement?.querySelector('[data-zv-success-template]')
      || form.closest('[data-zv-form-scope]')?.querySelector('[data-zv-success-template]');
    if (template instanceof HTMLTemplateElement) {
      const scrollLeft = window.scrollX;
      const scrollTop = window.scrollY;
      const layoutGuard = form.hasAttribute('data-zv-success-preserve-layout')
        ? form.querySelector('[data-zv-form-guard]') : null;
      if (layoutGuard) {
        // Retain only the layout footprint; the completed fields are hidden,
        // disabled and inert. A shared grid keeps the receipt card in place.
        layoutGuard.disabled = true;
        layoutGuard.inert = true;
        layoutGuard.setAttribute('aria-hidden', 'true');
        form.dataset.zvSuccessComplete = 'true';
        form.querySelector(':scope > [data-zv-success-content]')?.remove();
        form.append(template.content.cloneNode(true));
      } else {
        form.replaceChildren(template.content.cloneNode(true));
      }
      const successTarget = form.querySelector('[data-zv-success-content]')
        || form.querySelector('[tabindex="-1"]');
      try {
        successTarget?.focus({ preventScroll: true });
      } catch (error) {
        successTarget?.focus?.();
      }
      window.requestAnimationFrame(() => {
        try {
          window.scrollTo({ left: scrollLeft, top: scrollTop, behavior: 'auto' });
        } catch (error) {
          window.scrollTo(scrollLeft, scrollTop);
        }
      });
      return;
    }

    const hideSelector = form.dataset.zvSuccessHide;
    const showSelector = form.dataset.zvSuccessShow;
    const scope = form.closest('[data-zv-form-scope]') || form.parentElement;
    if (hideSelector) scope?.querySelectorAll(hideSelector).forEach((element) => { element.hidden = true; });
    if (showSelector) scope?.querySelectorAll(showSelector).forEach((element) => {
      element.hidden = false;
      element.focus?.();
    });
  };

  const syncOneContactValidity = (form) => {
    if (!form?.hasAttribute('data-zv-require-one-contact')) return true;
    const fields = Array.from(form.querySelectorAll('[data-zv-contact-option]'));
    if (!fields.length) return true;
    const hasContact = fields.some((field) => String(field.value || '').trim().length > 0);
    fields.forEach((field) => field.setCustomValidity?.(''));
    if (!hasContact) fields[0].setCustomValidity?.(window.ZinVoltI18n.t("enter_a_phone_number_or_email_address_077f20"));
    return hasContact;
  };

  const bindForm = (form) => {
    if (!form || form.dataset.zvExternalSubmit === 'true' || form.dataset.zvFormBound === 'true') return;
    // Do not unlock a two-step form if its UI/validation controller failed.
    if (form.hasAttribute('data-zv-advice-intake') && form.dataset.zvIntakeReady !== 'true') return;
    clearError(form);
    form.addEventListener('input', () => clearPreviewStatus(form));
    if (form.hasAttribute('data-zv-require-one-contact')) {
      form.querySelectorAll('[data-zv-contact-option]').forEach((field) => {
        field.addEventListener('input', () => syncOneContactValidity(form));
      });
      syncOneContactValidity(form);
    }
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formId = String(form.dataset.zvFormId || '').trim();
      if (form.dataset.zvFormBusy === 'true' || (formId && inFlightByFormId.has(formId))) return;

      const cooldownRemaining = recentSuccessRemainingMs(formId);
      if (cooldownRemaining) {
        showError(form, duplicateSubmissionMessage(cooldownRemaining));
        return;
      }

      syncOneContactValidity(form);
      if (!form.checkValidity()) {
        form.reportValidity?.();
        return;
      }
      clearError(form);
      clearPreviewStatus(form);
      if (formId) setBusyForFormId(formId, true);
      else setBusy(form, true);
      try {
        const result = await submit(form, { previewCheck: true });
        if (result?.previewOnly === true) showPreviewStatus(form);
        else showDefaultSuccess(form);
      } catch (error) {
        const message = error?.message || window.ZinVoltI18n.t("something_went_wrong_while_sending_please_try_60c48b");
        showError(form, message);
        document.dispatchEvent(new CustomEvent('zv:shopify-form-error', {
          detail: { formId: form.dataset.zvFormId, formName: form.dataset.zvFormName, form, message }
        }));
      } finally {
        if (formId) setBusyForFormId(formId, false);
        else if (document.documentElement.contains(form)) setBusy(form, false);
      }
    });
    // Unlock only after the preventDefault handler exists. If loading/binding
    // fails, the disabled fieldset and visible contact fallback stay intact.
    form.dataset.zvFormBound = 'true';
    form.querySelectorAll('[data-zv-form-guard]').forEach((fieldset) => { fieldset.disabled = false; });
    form.querySelectorAll('[data-zv-form-initialisation-notice]').forEach((notice) => { notice.hidden = true; });
  };

  const initialise = (root = document) => {
    if (root.matches?.(ENGINE_SELECTOR)) removeHiddenFormFromTabOrder(root);
    root.querySelectorAll?.(ENGINE_SELECTOR).forEach(removeHiddenFormFromTabOrder);
    const forms = [];
    if (root.matches?.(SOURCE_SELECTOR)) forms.push(root);
    root.querySelectorAll?.(SOURCE_SELECTOR).forEach((form) => forms.push(form));
    Array.from(new Set(forms)).forEach(bindForm);
  };

  window[GLOBAL_KEY] = { initialise, submit, normalise, createTrackingContext };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
